import { createSlice } from '@reduxjs/toolkit';
import { arrayToDictionary } from '../../utils/arrayToDictionary';
import {
  copyFormEditorFormThunk,
  deleteFormEditorFormThunk,
  getEmployerBusinessesThunk,
  getFormEditorFormThunk,
  saveFormEditorFormThunk,
  updateFormEditorFormThunk,
} from '../thunks/formEditorThunks';
import { v4 } from 'uuid';

export const formEditorSlice = createSlice({
  name: 'formEditor',
  initialState: {
    isLoading: false,
    selectedFormId: null,
    formEditorForms: {},
    businesses: [],
    selectedInputFieldId: null,
    hasUnsavedChanges: false,
    isAutoSave: false,
    inputFieldOpacity: 0.7,
    uiForShowingForms: 'list',
  },
  reducers: {
    initFormsFromLocalstorage: (state, action) => {
      const formEditorForms = localStorage.getItem('formEditorForms');
      const isAutoSave = localStorage.getItem('isAutoSaveFormEditor');
      state.isAutoSave = isAutoSave;
      state.formEditorForms = JSON.parse(formEditorForms);
    },
    setIsAutoSaveFormEditor: (state, action) => {
      state.isAutoSave = action.payload;
      localStorage.setItem('isAutoSaveFormEditor', action.payload);
    },
    changeUiForShowingForms: (state, action) => {
      if (state.uiForShowingForms === 'grid') {
        state.uiForShowingForms = 'list';
      } else {
        state.uiForShowingForms = 'grid';
      }
    },
    setInputFieldOpacity: (state, action) => {
      state.inputFieldOpacity = action.payload;
    },
    selectForm: (state, action) => {
      state.selectedFormId = action.payload.id;
    },
    unselectForm: (state, action) => {
      state.selectedFormId = null;
      state.selectedInputFieldId = null;
    },

    // meaby unneccesary because we call unselectForm when component dismounts
    createNewForm: (state, action) => {
      state.selectedFormId = null;
      state.selectedInputFieldId = null;
    },

    addInputField: (state, action) => {
      const { inputField, pageIndex = 0 } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      // Ensure inputFields is an array of arrays
      if (!Array.isArray(selectedFormEditorForm.inputFields)) {
        selectedFormEditorForm.inputFields = [];
      }

      // Ensure the page array exists
      if (!selectedFormEditorForm.inputFields[pageIndex]) {
        selectedFormEditorForm.inputFields[pageIndex] = [];
      }

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = [
        ...updatedInputFields[pageIndex],
        inputField,
      ];

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    addNameForTheForm: (state, action) => {
      const name = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedForm = {
        ...selectedFormEditorForm,
        name,
      };

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    updateBusinessIdForTheForm: (state, action) => {
      const businessId = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedForm = {
        ...selectedFormEditorForm,
        businessId,
      };

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    removeBusinessIdForTheForm: (state, action) => {
      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedForm = {
        ...selectedFormEditorForm,
        businessId: null,
      };

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    selectInputField: (state, action) => {
      state.selectedInputFieldId = action.payload.id;
    },
    unSelectInputField: (state, action) => {
      state.selectedInputFieldId = null;
    },
    deleteInputField: (state, action) => {
      const { id, pageIndex } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      if (updatedInputFields[pageIndex]) {
        updatedInputFields[pageIndex] = updatedInputFields[pageIndex].filter(
          (field) => field.id !== id
        );
      }

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
      state.selectedInputFieldId = null;
    },
    copyInputField: (state, action) => {
      const { id, pageIndex } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const selectedInputField = selectedFormEditorForm.inputFields[
        pageIndex
      ]?.find((field) => field.id === id);

      if (!selectedInputField) return;

      const copyOfSelectedInputField = {
        ...selectedInputField,
        id: v4(),
        x: selectedInputField.x + 100,
      };

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      if (!updatedInputFields[pageIndex]) {
        updatedInputFields[pageIndex] = [];
      }
      updatedInputFields[pageIndex] = [
        ...updatedInputFields[pageIndex],
        copyOfSelectedInputField,
      ];

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;

      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );

      state.selectedInputFieldId = null;
    },

    onInputFieldChange: (state, action) => {
      const { updatedInputField, pageIndex = 0 } = action.payload;
      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      const pageFields = [...updatedInputFields[pageIndex]];
      const fieldIndex = pageFields.findIndex(
        (field) => field.id === updatedInputField.id
      );

      if (fieldIndex !== -1) {
        pageFields[fieldIndex] = updatedInputField;
        updatedInputFields[pageIndex] = pageFields;
      }

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    formUpdateAfterTextract: (state, action) => {
      const formEditorForm = action.payload;

      state.formEditorForms = {
        ...state.formEditorForms,
        [formEditorForm.id]: formEditorForm,
      };

      state.selectedFormId = formEditorForm.id;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    setFormEditorFormsFromLocalstorage: (state, action) => {
      const formEditorForms = action.payload;
      state.formEditorForms = formEditorForms;
    },
  },
  extraReducers(builder) {
    builder
      // GET
      .addCase(getFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorForms = action.payload;
        state.formEditorForms = arrayToDictionary(formEditorForms);
        state.isLoading = false;
        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );
      })
      .addCase(getFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // CREATE
      .addCase(saveFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(saveFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorFormDraft = action.payload;

        state.formEditorForms = {
          ...state.formEditorForms,
          [formEditorFormDraft.id]: formEditorFormDraft,
        };
        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );

        state.selectedFormId = formEditorFormDraft.id;
        state.hasUnsavedChanges = false;
        state.isLoading = false;
      })
      .addCase(saveFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // UPDATE
      .addCase(updateFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorForm = action.payload;

        state.formEditorForms = {
          ...state.formEditorForms,
          [formEditorForm.id]: formEditorForm,
        };
        state.hasUnsavedChanges = false;

        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );
      })
      .addCase(updateFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // EMPLOYER GET
      .addCase(getEmployerBusinessesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businesses = action.payload;
      })
      .addCase(getEmployerBusinessesThunk.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(getEmployerBusinessesThunk.pending, (state, action) => {
        state.isLoading = true;
      })

      // COPY
      .addCase(copyFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(copyFormEditorFormThunk.fulfilled, (state, action) => {
        const formCopy = action.payload;

        state.formEditorForms = {
          ...state.formEditorForms,
          [formCopy.id]: formCopy,
        };
        state.isLoading = false;
      })
      .addCase(copyFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })
      // DELETE
      .addCase(deleteFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteFormEditorFormThunk.fulfilled, (state, action) => {
        const formId = action.payload.id;

        delete state.formEditorForms[formId];
        state.isLoading = false;
      })
      .addCase(deleteFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

// ACTIONS
export const {
  initFormsFromLocalstorage,
  addInputField,
  selectInputField,
  unSelectInputField,
  onInputFieldChange,
  setInputFields,
  textractGeneratedFields,
  setFormEditorInputFieldsFromLocalstorage,
  deleteInputField,
  copyInputField,
  createNewForm,
  formUpdateAfterTextract,
  selectForm,
  unselectForm,
  addNameForTheForm,
  setIsAutoSaveFormEditor,
  setInputFieldOpacity,
  updateBusinessIdForTheForm,
  removeBusinessIdForTheForm,
  changeUiForShowingForms,
} = formEditorSlice.actions;

// SELECTORS

export const getFormEditorControlData = (state) => {
  const slice = state.formEditorSlice;

  return {
    uiForShowingForms: slice?.uiForShowingForms,
    isLoading: slice?.isLoading,
    hasUnsavedChanges: slice?.hasUnsavedChanges,
    selectedFormId: slice?.selectedFormId,
    formEditorForms: Object.values(slice?.formEditorForms),
    selectedInputFieldId: slice?.selectedInputFieldId,
    inputFieldOpacity: slice?.inputFieldOpacity,
    selectedInputField: (() => {
      if (!slice?.selectedInputFieldId || !slice?.selectedFormId) return null;
      const form = slice.formEditorForms[slice.selectedFormId];
      if (!form?.inputFields) return null;

      // Search through all pages to find the selected field
      for (const pageFields of form.inputFields) {
        if (Array.isArray(pageFields)) {
          const field = pageFields.find(
            (field) => field.id === slice.selectedInputFieldId
          );
          if (field) return field;
        }
      }
      return null;
    })(),
  };
};

export const getUnpublishedFormEditorForms = (state) => {
  const slice = state.formEditorSlice;
  const formEditorForms = Object.values(slice?.formEditorForms);

  const unpublished = formEditorForms.filter((form) => {
    return form?.businessId === null;
  });

  return unpublished;
};

export const getPublishedFormEditorForms = (state) => {
  const slice = state.formEditorSlice;
  const formEditorForms = Object.values(slice?.formEditorForms);

  const published = formEditorForms?.filter((form) => {
    return form?.businessId !== null;
  });

  return published;
};

export const getSelectedFormEditorForm = (state) => {
  const slice = state.formEditorSlice;

  if (!slice?.selectedFormId) return null;
  const formEditorForm = slice?.formEditorForms[slice.selectedFormId];

  return {
    ...formEditorForm,
    inputFields: formEditorForm?.inputFields || [],
  };
};

export const getFormEditorIsAutoSave = (state) => {
  const slice = state.formEditorSlice;
  return slice.isAutoSave;
};

// input fields are dictionary here
export const getSelectedFormEditorFormForDynamoDB = (state) => {
  const slice = state.formEditorSlice;

  if (!slice?.selectedFormId) return null;
  const formEditorForm = slice.formEditorForms[slice.selectedFormId];
  return formEditorForm;
};

export const getEmployerBusinesses = (state) =>
  state.formEditorSlice.businesses;
